import { Box, Card, Flex } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import {
  applyEdgeChanges,
  Background,
  Controls,
  type Edge,
  type EdgeChange,
  MarkerType,
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
} from '@xyflow/react'
import { useCallback, useEffect, useState } from 'react'
import '@xyflow/react/dist/style.css'
import BackendNodeDT from '~/components/nodes/BackendNodeDT'
import CloudNodeDT from '~/components/nodes/cloudNodeDT'
import Dash0NodeDT from '~/components/nodes/dash0NodeDT'
import EdgeBoxNodeDT from '~/components/nodes/edgeBoxNodeDT'
import HeaterNodeDestination from '~/components/nodes/heaterNodeDestination'
import MobileSourceNode from '~/components/nodes/mobileSourceNode'
import { PageHeader } from '~/components/shell'

export const Route = createFileRoute('/_app/connected/tracing')({
  component: ConnectedDistributedTracing,
})

function ConnectedDistributedTracing() {
  const useToggle = (initialState: boolean): [boolean, () => void] => {
    const [isToggled, setIsToggled] = useState(initialState)
    const toggle = useCallback(() => setIsToggled((state) => !state), [])
    return [isToggled, toggle]
  }

  const [heaterState, setHeaterState] = useToggle(false)

  const changeHeaterState = () => {
    console.log('Changing state')
    setHeaterState()
  }

  const initNodes = [
    {
      id: '1',
      data: {
        label: 'User',
        heaterState: heaterState,
        onChange: changeHeaterState,
      },
      type: 'mobileSourceNode',
      position: { x: -300, y: 250 },
      style: { borderColor: '#10b981' },
    },
    {
      id: '2',
      data: {
        label: 'Backend API',
        heaterState: undefined,
        onChange: undefined,
      },
      type: 'backendNodeDT',
      position: { x: 0, y: 250 },
      style: { borderColor: '#10b981' },
    },
    {
      id: '10',
      data: {
        label: 'HiveMQ Cloud',
        heaterState: undefined,
        onChange: undefined,
      },
      type: 'cloudNodeDT',
      position: { x: 300, y: 250 },
    },
    {
      id: '20',
      data: { label: 'Edge Box', heaterState: undefined, onChange: undefined },
      type: 'edgeBoxNodeDT',
      position: { x: 800, y: 250 },
      style: { borderColor: '#10b981' },
    },
    {
      id: '30',
      data: { label: 'Heaters', heaterState: heaterState, onChange: undefined },
      type: 'heaterNodeDestination',
      position: { x: 1100, y: 250 },
      style: { borderColor: '#10b981' },
    },
    {
      id: '40',
      data: {
        label: 'Observability',
        heaterState: undefined,
        onChange: undefined,
      },
      type: 'dash0NodeDT',
      position: { x: 300, y: 700 },
      style: { borderColor: '#10b981' },
    },
  ]

  const initEdges: Edge[] = [
    {
      id: '12',
      source: '1',
      target: '2',
      label: 'HTTPS',
      animated: true,
      style: { stroke: '#037DA5', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
    },
    {
      id: '140',
      source: '1',
      sourceHandle: 'b',
      target: '40',
      label: 'OTEL Formatted traces',
      animated: true,
      style: { stroke: 'red', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: 'red' },
    },
    {
      id: '210',
      source: '2',
      target: '10',
      label: 'MQTT',
      animated: true,
      style: { stroke: '#ffc000', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#ffc000' },
    },
    {
      id: '240',
      source: '2',
      sourceHandle: 'b',
      target: '40',
      label: 'OTEL Formatted traces',
      animated: true,
      style: { stroke: 'red', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: 'red' },
    },
    {
      id: '1020',
      source: '10',
      target: '20',
      label: 'MQTT',
      animated: true,
      style: { stroke: '#ffc000', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#ffc000' },
    },
    {
      id: '1040',
      source: '10',
      sourceHandle: 'b',
      target: '40',
      label: 'OTEL Formatted traces',
      animated: true,
      style: { stroke: 'red', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: 'red' },
    },
    {
      id: '2030',
      source: '20',
      target: '30',
      label: 'Zigbee',
      animated: true,
      style: { stroke: 'green', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: 'green' },
    },
    {
      id: '2040',
      source: '20',
      sourceHandle: 'b',
      target: '40',
      label: 'OTEL Formatted traces',
      animated: true,
      style: { stroke: 'red', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: 'red' },
    },
  ]

  const nodeTypes = {
    mobileSourceNode: MobileSourceNode,
    heaterNodeDestination: HeaterNodeDestination,
    edgeBoxNodeDT: EdgeBoxNodeDT,
    backendNodeDT: BackendNodeDT,
    cloudNodeDT: CloudNodeDT,
    dash0NodeDT: Dash0NodeDT,
  }

  const [nodes, setNodes, onNodesChange] = useNodesState(initNodes)
  const [edges, setEdges] = useState(initEdges)
  const [isLocked, setIsLocked] = useState(false)

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  )

  const onLockedChange = () => {
    setIsLocked(!isLocked)
  }

  useEffect(() => {
    // @ts-expect-error - Complex ReactFlow type inference issue
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === '30') {
          return {
            ...node,
            data: {
              ...node.data,
              heaterState: heaterState,
            },
          }
        }
        return node
      }),
    )
  }, [heaterState, setNodes])

  return (
    <Flex direction="column" h="100%" gap={4}>
      <PageHeader
        title="Connected - Tracing"
        subtitle="End-to-end observability with OpenTelemetry tracing across the connected building stack"
      />

      <Card.Root flex={1} minH={0} overflow="hidden">
        <Box h="100%" w="100%">
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              onNodesChange={onNodesChange}
              edges={edges}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              className="flow-customization"
              nodesDraggable={isLocked}
            >
              <Background className="flow-tracing-background" />
              <Controls onInteractiveChange={onLockedChange} />
            </ReactFlow>
          </ReactFlowProvider>
        </Box>
      </Card.Root>
    </Flex>
  )
}
